<script>
    import { onMount } from 'svelte';
    import { userStore } from '../store.js';
    import { navigate } from '../router.js';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import Chip from '../components/Chip.svelte';
    import Banner from '../components/Banner.svelte';
    import EmptyState from '../components/EmptyState.svelte';

    let entries = [];
    let trends = [];
    let isLoading = true;
    let error = '';
    let needsSignIn = false;

    $: isLoggedIn = $userStore.isAuthenticated;
    $: latest = entries[0];

    // When an empty / sign-in / error state renders, THAT block owns the
    // screen's single call to action. Showing hero actions too put two rose
    // primaries on screen pointing at the same place, which is the exact rule
    // the Button component's own docs lay down. Caught by migrating this page.
    $: stateOwnsCta = isLoading || needsSignIn || !!error || entries.length === 0;

    onMount(async () => {
        await loadProgress();
    });

    async function loadProgress() {
        isLoading = true;
        error = '';
        needsSignIn = false;
        try {
            const response = await fetch('/api/progress', { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                entries = data.entries || [];
                trends = data.trends || [];
            } else if (response.status === 401) {
                // Not an error. Signed-out is an expected state with its own
                // next step, so it gets an empty state, not a red banner.
                needsSignIn = true;
            } else {
                error = 'Unable to load progress right now.';
            }
        } catch (e) {
            console.error('Progress load error:', e);
            error = 'Unable to load progress right now.';
        }
        isLoading = false;
    }

    function formatDate(value) {
        if (!value) return 'Unknown date';
        return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }

    function concernLabel(concern) {
        return typeof concern === 'string' ? concern : concern.label || concern.concern || '';
    }

    function concernScore(concern) {
        if (typeof concern === 'string' || concern.score === null || concern.score === undefined) return null;
        return `${concern.score}%`;
    }

    function displayablePictureUrl(url) {
        if (!url || url === 'analysis' || String(url).startsWith('local://')) {
            return '';
        }

        return url;
    }

    function trendCopy(trend) {
        // It takes two scored photos to say anything about a direction. Before
        // that the honest answer is that we have nowhere to measure from.
        if (trend.direction === 'unknown') {
            return trend.measuredCount === 1 ? 'first reading' : 'not measured yet';
        }
        if (trend.direction === 'improving') return `${Math.abs(trend.change)} pts better`;
        if (trend.direction === 'watch') return `${Math.abs(trend.change)} pts higher`;
        return 'no change yet';
    }

    // The bar showed 50% whenever a score was missing, which looked like a
    // measurement of exactly half. An unscored concern gets no bar.
    function barWidth(trend) {
        return typeof trend.latestScore === 'number' ? Math.min(100, trend.latestScore) : 0;
    }

    function goToFaceAnalysis() {
        navigate('face-analysis');
    }

    function signIn() {
        window.location.href = '/api/login?currentPage=progress';
    }
</script>

<style>
    .hero {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: var(--space-10);
        align-items: end;
        margin-bottom: var(--space-8);
    }

    .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
    }

    .eyebrow {
        color: var(--sage);
        font-size: var(--text-2xs);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        margin-bottom: var(--space-4);
    }

    h1, h2, h3 {
        font-family: var(--font-display);
        font-weight: var(--weight-regular);
        margin: 0;
    }

    h1 {
        font-size: var(--text-4xl);
        line-height: var(--leading-tight);
    }

    h2 { font-size: var(--text-xl); }
    h3 { font-size: var(--text-base); }

    p {
        color: var(--text-muted);
        line-height: var(--leading-relaxed);
    }

    .grid {
        display: grid;
        grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
        gap: var(--space-6);
        align-items: start;
    }

    .latest-image {
        width: 100%;
        max-height: 420px;
        object-fit: contain;
        background: var(--bg);
        border: 1px solid var(--border);
    }

    .image-placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 180px;
        background: var(--scrim);
        border: 1px solid var(--border);
        color: var(--text-muted);
        font-size: var(--text-xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        text-align: center;
    }

    .metric-list {
        display: grid;
        gap: var(--space-3);
        margin-top: var(--space-5);
    }

    .metric {
        display: grid;
        grid-template-columns: 150px 1fr 88px;
        gap: var(--space-3);
        align-items: center;
        color: var(--cream);
        font-size: var(--text-sm);
    }

    .metric > span:last-child { white-space: nowrap; }

    /* Not a primitive yet. Recommendations may want the same bar; if it does,
       that is the second caller and the point at which it becomes a Meter. */
    .bar {
        height: var(--space-2);
        background: var(--hover-veil);
        overflow: hidden;
    }

    .bar span {
        display: block;
        height: 100%;
        background: linear-gradient(90deg, var(--sage), var(--gold));
    }

    .bar span.watch { background: linear-gradient(90deg, var(--caution), var(--gold)); }
    .bar span.improving { background: linear-gradient(90deg, var(--sage), var(--success)); }

    /* Nothing to draw: a dashed rule reads as absent rather than as zero. */
    .bar.unmeasured {
        background: none;
        border-top: 1px dashed var(--border-strong);
        height: 0;
        align-self: center;
    }

    .timeline {
        display: grid;
        gap: var(--space-4);
        margin-top: var(--space-5);
    }

    .entry {
        display: grid;
        grid-template-columns: 90px 1fr;
        gap: var(--space-4);
        align-items: center;
        padding: var(--space-4);
        background: var(--scrim);
        border: 1px solid var(--border);
    }

    .entry img {
        width: 90px;
        height: 90px;
        object-fit: cover;
        background: var(--bg);
    }

    .entry .image-placeholder {
        width: 90px;
        height: 90px;
        min-height: 90px;
        font-size: var(--text-3xs);
        line-height: var(--leading-snug);
        padding: var(--space-2);
    }

    .tags {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
        margin-top: var(--space-3);
    }

    .loading {
        text-align: center;
        padding: var(--space-16) var(--space-6);
        color: var(--text-muted);
        font-size: var(--text-sm);
    }

    /* Svelte's style scoping does not reach a child COMPONENT's root element,
       so a plain `.stack > * + *` compiles to an unused selector and silently
       does nothing. :global() on the child part is the fix. */
    .stack > :global(* + *) { margin-top: var(--space-6); }

    @media (max-width: 768px) {
        .hero,
        .grid {
            grid-template-columns: 1fr;
        }

        .metric {
            grid-template-columns: 1fr;
        }
    }
</style>

<div class="u-page">
    <section class="hero">
        <div>
            <div class="eyebrow">Progress Tracking</div>
            <h1>Your skin, measured over time.</h1>
            <p>Use regular photo check-ins to compare concerns, routine changes, and visible improvement instead of guessing whether products are working.</p>
        </div>
        {#if !stateOwnsCta}
            <div class="hero-actions">
                <Button on:click={goToFaceAnalysis}>New check-in</Button>
                {#if !isLoggedIn}
                    <Button variant="secondary" on:click={signIn}>Sign in to save</Button>
                {/if}
            </div>
        {/if}
    </section>

    {#if isLoading}
        <Card><div class="loading">Loading progress…</div></Card>
    {:else if needsSignIn}
        <EmptyState
            title="Sign in to see your progress."
            body="Progress photos are saved to your account so you can compare them across devices."
        >
            <Button on:click={signIn}>Sign in</Button>
        </EmptyState>
    {:else if error}
        <div class="stack">
            <Banner tone="danger" title="We couldn't load your progress">{error}</Banner>
            <Button variant="secondary" on:click={loadProgress}>Try again</Button>
        </div>
    {:else if entries.length === 0}
        <EmptyState
            title="No progress photos yet."
            body="Take a face analysis now, then come back weekly or monthly to see what changes."
        >
            <Button on:click={goToFaceAnalysis}>Start first check-in</Button>
        </EmptyState>
    {:else}
        <div class="stack">
            <div class="grid">
                <Card>
                    <h2>Latest check-in</h2>
                    <p>{formatDate(latest.date)}</p>
                    {#if displayablePictureUrl(latest.pictureUrl)}
                        <img class="latest-image" src={displayablePictureUrl(latest.pictureUrl)} alt="Latest skin progress" />
                    {:else}
                        <div class="image-placeholder">Photo not available for this check-in</div>
                    {/if}
                    <div class="tags">
                        {#each latest.concerns || [] as concern}
                            <Chip value={concernScore(concern)}>{concernLabel(concern)}</Chip>
                        {/each}
                    </div>
                </Card>

                <Card>
                    <h2>Concern trends</h2>
                    <p>Lower concern scores over time usually mean improvement. A direction needs two photos we could score, so a new concern shows its first reading and nothing more. Use this with photos and routine-change dates, not as a medical diagnosis.</p>
                    <div class="metric-list">
                        {#each trends as metric}
                            <div class="metric">
                                <span>{metric.label}</span>
                                {#if metric.direction === 'unknown'}
                                    <!-- An empty track reads as a score of zero, which
                                         would be the opposite mistake to the old 50. -->
                                    <div class="bar unmeasured" aria-label="not measured"></div>
                                {:else}
                                    <div class="bar"><span class={metric.direction} style={`width: ${barWidth(metric)}%`}></span></div>
                                {/if}
                                <span>{trendCopy(metric)}</span>
                            </div>
                        {/each}
                    </div>
                </Card>
            </div>

            <Card>
                <h2>Photo timeline</h2>
                <div class="timeline">
                    {#each entries as entry}
                        <div class="entry">
                            {#if displayablePictureUrl(entry.pictureUrl)}
                                <img src={displayablePictureUrl(entry.pictureUrl)} alt="Skin progress from {formatDate(entry.date)}" />
                            {:else}
                                <div class="image-placeholder">No photo</div>
                            {/if}
                            <div>
                                <h3>{formatDate(entry.date)}</h3>
                                <div class="tags">
                                    {#each entry.concerns || [] as concern}
                                        <Chip value={concernScore(concern)}>{concernLabel(concern)}</Chip>
                                    {/each}
                                </div>
                            </div>
                        </div>
                    {/each}
                </div>
            </Card>
        </div>
    {/if}
</div>
